/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useSelector } from 'react-redux';

import { breakpoints } from '@coursera/cds-core';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { Footer, Header, LoadingView, Messages, Sender } from 'bundles/ai-course-coach/components/Conversation';
import type { GlobalState } from 'bundles/ai-course-coach/store/types';
import type { AnyFunction, HomeView, SendMessageFunc } from 'bundles/ai-course-coach/utils/types';

const styles = {
  conversationContainer: css`
    height: 100%;
  `,
  conversation: css`
    background-color: var(--cds-color-neutral-background-primary);
    height: 100%;
    width: 100%;
    display: flex;
    flex-flow: column;
    z-index: 10;
    border: none;
    border-radius: var(--cds-border-radius-200);
    position: relative;

    ${breakpoints.down('xs')} {
      border-radius: 0;
    }

    .coach-message {
      background-color: var(--cds-color-neutral-primary-invert);
    }

    .coach-message-client {
      background-color: none;
    }

    &.active {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.3s ease, transform 0.3s ease;
    }

    &.hidden {
      z-index: -1;
      pointer-events: none;
      opacity: 0;
      transform: translateY(10px);
      transition: opacity 0.3s ease, transform 0.3s ease;
    }
  `,
  headerContainer: css`
    flex-grow: 0;
    border-bottom: 1px solid var(--cds-color-grey-100);
  `,
  messageContainer: css`
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
    justify-content: center;
    max-width: 100%;
    position: relative;
  `,
  messageContainerResponses: css`
    max-width: 770px;
    margin: 0 auto;
  `,
  senderContainer: css`
    flex-grow: 0;
    width: 100%;
    max-width: 770px;
    margin: 0 auto;
    border-top: 1px solid var(--cds-color-grey-100);
  `,
  footerContainer: css`
    flex-grow: 0;
    max-width: 770px;
    margin: 0 auto;
  `,
};

export type ConversationProps = {
  senderPlaceHolder: string;
  showCloseButton: boolean;
  showFullScreenButton: boolean;
  isFullScreen: boolean;
  disabledInput: boolean;
  autofocus: boolean;
  sendMessage: SendMessageFunc;
  toggleChat: AnyFunction;
  toggleFullScreen?: () => void;
  profileClientAvatar?: string;
  onTextInputChange?: (text: string) => void;
  handleNewSession?: () => void;
  homeView?: HomeView;
  isChatHistoryLoading?: boolean;
  courseId?: string;
};

/**
 * Standalone component that handles all the conversations with Coach.
 */
const Conversation = ({
  senderPlaceHolder,
  showCloseButton,
  showFullScreenButton,
  isFullScreen,
  disabledInput,
  autofocus,
  sendMessage,
  toggleChat,
  profileClientAvatar,
  onTextInputChange,
  handleNewSession,
  homeView,
  isChatHistoryLoading,
  courseId,
  toggleFullScreen,
}: ConversationProps) => {
  const { isMessageLoading } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
  }));

  return (
    <div css={styles.conversation}>
      <div css={styles.headerContainer}>
        <Header
          toggleChat={toggleChat}
          showCloseButton={showCloseButton}
          showFullScreenButton={showFullScreenButton}
          toggleFullScreen={toggleFullScreen}
          isFullScreen={isFullScreen}
          handleNewSession={handleNewSession}
          loading={isChatHistoryLoading || isMessageLoading}
        />
      </div>

      <div css={styles.messageContainer}>
        <div css={styles.messageContainerResponses}>
          {isChatHistoryLoading && <LoadingView />}
          {!isChatHistoryLoading && (
            <Messages
              profileClientAvatar={profileClientAvatar}
              homeView={homeView}
              sendMessage={sendMessage}
              courseId={courseId}
            />
          )}
        </div>
      </div>

      <div css={styles.senderContainer}>
        <Sender
          sendMessage={sendMessage}
          placeholder={senderPlaceHolder}
          disabledInput={disabledInput}
          autofocus={autofocus}
          onTextInputChange={onTextInputChange}
        />
      </div>
      <div css={styles.footerContainer}>
        <Footer />
      </div>
    </div>
  );
};

export default Conversation;
